/**
 * Widget Event Emitter
 *
 * Utility class for emitting events from inside the widget (Shadow DOM)
 * to the parent window using custom events.
 * Only active when running in embedded mode with a widgetId (backward compatible).
 */

import { WidgetEventName, WidgetEventPayload } from './widget-events.types';

export class WidgetEventEmitter {
  constructor(private widgetId?: string) {}

  /**
   * Emit an event to the parent window via custom events
   * No-op if not in event mode (widgetId is null/undefined)
   */
  emit(eventName: WidgetEventName, payload: WidgetEventPayload): void {
    if (!this.widgetId) {
      // No-op in legacy mode - backward compatible
      return;
    }

    try {
      const customEvent = new CustomEvent('fabric-widget-event', {
        detail: {
          type: 'fabric-widget-event',
          eventName,
          widgetId: this.widgetId,
          detail: payload,
        },
        bubbles: true,
        cancelable: true,
      });

      window.dispatchEvent(customEvent);
    } catch (error) {
      // Silently fail if custom events are not available
      // This can happen in some restricted environments
      console.warn('Failed to emit widget event:', error);
    }
  }

  /**
   * Check if the emitter is in event mode
   */
  isEventMode(): boolean {
    return !!this.widgetId;
  }

  /**
   * Get the widget ID
   */
  getWidgetId(): string | undefined {
    return this.widgetId;
  }
}
